const DEFAULT_ASSIGNEE_TYPES = {
  ALL: 'all',
};

export const CHAT_LIST_INITIAL_ROUTE_BACKGROUND_MODE = 'background';
export const CHAT_LIST_INITIAL_ROUTE_SEARCH_MODE = 'search';
export const CHAT_LIST_PAGE_CHANGE_NOOP_STATUS = 'noop';
export const CHAT_LIST_PAGE_CHANGE_SEARCH_STATUS = 'search';
export const CHAT_LIST_PAGE_CHANGE_NORMAL_STATUS = 'normal';
export const CHAT_LIST_SEARCH_QUERY_ACTION_CLEAR = 'clear';
export const CHAT_LIST_SEARCH_QUERY_ACTION_APPLY_ACTIVE = 'apply-active';
export const CHAT_LIST_SEARCH_QUERY_ACTION_NOOP = 'noop';
export const CHAT_LIST_SEARCH_INCLUDE_MESSAGE_SEARCH_FLAG = 'true';
export const CHAT_LIST_SEARCH_PREVIEW_FLAG = 'true';
export const CHAT_LIST_INCLUDE_LABELED_FILTER_FLAG = 'true';

export const resolveChatListTotalPages = ({
  itemsPerPage = 30,
  totalItems = 0,
} = {}) => {
  const perPage = Number(itemsPerPage);
  if (!Number.isFinite(perPage) || perPage <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil(Number(totalItems || 0) / perPage));
};

export const hasChatListAppliedFilters = (appliedFilters = []) =>
  Array.isArray(appliedFilters) && appliedFilters.length !== 0;

export const hasChatListPriorityConversations = (conversationList = []) => {
  const conversations = Array.isArray(conversationList) ? conversationList : [];
  return conversations.some((conversation) => conversation?.priority != null);
};

export const resolveChatListCurrentPageFilterKey = ({
  activeAssigneeTab = '',
  hasAppliedFiltersOrActiveFolders = false,
} = {}) =>
  hasAppliedFiltersOrActiveFolders ? 'appliedFilters' : activeAssigneeTab;

export const shouldShowChatListAssignee = ({
  activeAssigneeTab = '',
  allAssigneeType = DEFAULT_ASSIGNEE_TYPES.ALL,
  hasAppliedFiltersOrActiveFolders = false,
} = {}) =>
  hasAppliedFiltersOrActiveFolders || activeAssigneeTab === allAssigneeType;

export const resolveChatListSearchContactId = ({
  contactId = undefined,
  currentContactId = null,
} = {}) => (contactId === undefined ? currentContactId : (contactId ?? null));

export const buildChatListSearchRequestParams = ({
  contactId = null,
  itemsPerPage = 30,
  page = 1,
  query = '',
  selectedAssigneeIdFilter = null,
} = {}) => {
  const params = {
    include_message_search: CHAT_LIST_SEARCH_INCLUDE_MESSAGE_SEARCH_FLAG,
    limit: itemsPerPage + 1,
    page,
    page_size: itemsPerPage,
    preview: CHAT_LIST_SEARCH_PREVIEW_FLAG,
    q: query,
  };

  if (contactId !== null && contactId !== undefined && contactId !== '') {
    params.contact_id = contactId;
  }

  if (selectedAssigneeIdFilter) {
    params.assignee_id = selectedAssigneeIdFilter;
  }

  return params;
};

export const extractChatListSearchPayload = (response = null) =>
  response?.data?.payload?.conversations || [];

export const shouldApplyChatListSearchResponse = ({
  currentRequestId = null,
  isComponentDestroyed = false,
  requestId = null,
} = {}) => !isComponentDestroyed && currentRequestId === requestId;

// append (Otello da mobile, scorrimento continuo): la pagina successiva si
// accoda in silenzio, senza lo spinner che sostituisce la lista.
export const buildChatListSearchStartState = ({
  page = 1,
  query = '',
  requestId = 0,
  append = false,
} = {}) => ({
  activeSearchQuery: query,
  currentPage: page,
  isSearchModeActive: true,
  searchQuery: query,
  searchResultsError: false,
  searchResultsLoading: !append,
  searchResultsRequestId: requestId + 1,
});

// Risultati gia' in lista piu' la pagina nuova, senza ripetere una
// conversazione arrivata in due pagine.
export const appendChatListSearchResults = (previous = [], next = []) => {
  const safePrevious = Array.isArray(previous) ? previous : [];
  const seen = new Set(safePrevious.map((item) => item?.id));
  const appended = (Array.isArray(next) ? next : []).filter((item) => {
    if (seen.has(item?.id)) return false;
    seen.add(item?.id);
    return true;
  });
  return [...safePrevious, ...appended];
};

export const resolveChatListSearchResultsState = ({
  currentPage = 1,
  itemsPerPage = 30,
  payload = [],
} = {}) => {
  const results = Array.isArray(payload) ? payload : [];
  const hasMore = results.length > itemsPerPage;
  const pageItems = hasMore ? results.slice(0, itemsPerPage) : results;
  const searchResultsTotal =
    (currentPage - 1) * itemsPerPage + pageItems.length + (hasMore ? 1 : 0);

  return {
    hasMore,
    pageItems,
    searchResultsTotal,
    totalPages: Math.max(1, currentPage + (hasMore ? 1 : 0)),
  };
};

export const buildChatListSearchSuccessState = ({
  currentPage = 1,
  itemsPerPage = 30,
  payload = [],
  append = false,
  previousResults = [],
} = {}) => {
  const searchState = resolveChatListSearchResultsState({
    currentPage,
    itemsPerPage,
    payload,
  });

  return {
    searchResults: append
      ? appendChatListSearchResults(previousResults, searchState.pageItems)
      : searchState.pageItems,
    searchResultsTotal: searchState.searchResultsTotal,
    totalPages: searchState.totalPages,
  };
};

export const buildChatListSearchErrorState = () => ({
  searchResults: [],
  searchResultsError: true,
  searchResultsTotal: 0,
  totalPages: 1,
});

export const normalizeChatListSearchQuery = (query = null) =>
  typeof query === 'string' ? query.trim() : '';

const readChatListSearchRunnerValue = (reader, fallback = null) => {
  if (typeof reader !== 'function') {
    return fallback;
  }

  try {
    return reader();
  } catch {
    return fallback;
  }
};

export const runChatListSearchResultsLoad = async ({
  append = false,
  getSearchResults = null,
  abortSearchRequest = null,
  applyState = null,
  clearBulkSelection = null,
  clearSearchResults = null,
  contactId = null,
  createAbortController = null,
  getCurrentPage = null,
  getIsComponentDestroyed = null,
  getSearchAbortController = null,
  getSearchResultsRequestId = null,
  isAbortError = null,
  itemsPerPage = 30,
  page = 1,
  query = '',
  searchConversations = null,
  searchResultsRequestId = 0,
  selectedAssigneeIdFilter = null,
  setSearchAbortController = null,
  syncSearchRouteQuery = null,
} = {}) => {
  const trimmed = normalizeChatListSearchQuery(query);

  if (!trimmed) {
    if (typeof clearSearchResults === 'function') {
      clearSearchResults();
    }
    return { query: trimmed, status: 'cleared' };
  }

  if (typeof abortSearchRequest === 'function') {
    abortSearchRequest();
  }

  const searchController =
    typeof createAbortController === 'function'
      ? createAbortController()
      : null;

  if (typeof setSearchAbortController === 'function') {
    setSearchAbortController(searchController);
  }

  const searchStartState = buildChatListSearchStartState({
    page,
    query: trimmed,
    requestId: searchResultsRequestId,
    append,
  });
  const requestId = searchStartState.searchResultsRequestId;

  if (typeof applyState === 'function') {
    applyState(searchStartState);
  }

  if (!append && typeof clearBulkSelection === 'function') {
    clearBulkSelection();
  }

  const shouldApplyResponse = () =>
    shouldApplyChatListSearchResponse({
      currentRequestId: readChatListSearchRunnerValue(
        getSearchResultsRequestId,
        requestId
      ),
      isComponentDestroyed: readChatListSearchRunnerValue(
        getIsComponentDestroyed,
        false
      ),
      requestId,
    });

  try {
    if (typeof syncSearchRouteQuery === 'function') {
      const routeSyncResult = syncSearchRouteQuery(trimmed);
      if (routeSyncResult && typeof routeSyncResult.catch === 'function') {
        routeSyncResult.catch(() => {});
      }
    }

    if (typeof searchConversations !== 'function') {
      return { query: trimmed, requestId, status: 'invalid' };
    }

    const params = buildChatListSearchRequestParams({
      contactId,
      itemsPerPage,
      page,
      query: trimmed,
      selectedAssigneeIdFilter,
    });
    const response = await searchConversations(
      params,
      searchController ? { signal: searchController.signal } : {}
    );
    const payload = extractChatListSearchPayload(response);

    if (!shouldApplyResponse()) {
      return { query: trimmed, requestId, status: 'stale' };
    }

    if (typeof applyState === 'function') {
      applyState(
        buildChatListSearchSuccessState({
          currentPage: readChatListSearchRunnerValue(getCurrentPage, page),
          itemsPerPage,
          payload,
          append,
          previousResults: readChatListSearchRunnerValue(getSearchResults, []),
        })
      );
    }

    return { query: trimmed, requestId, status: 'loaded' };
  } catch (error) {
    if (typeof isAbortError === 'function' && isAbortError(error)) {
      return { query: trimmed, requestId, status: 'aborted' };
    }

    if (!shouldApplyResponse()) {
      return { query: trimmed, requestId, status: 'stale' };
    }

    // Pagina successiva non arrivata: i risultati gia' visti restano e si
    // torna alla pagina di prima, cosi' scorrendo si riprova.
    if (typeof applyState === 'function') {
      applyState(
        append
          ? { currentPage: Math.max(1, page - 1) }
          : buildChatListSearchErrorState()
      );
    }

    return { query: trimmed, requestId, status: 'failed' };
  } finally {
    if (
      typeof getSearchAbortController === 'function' &&
      typeof setSearchAbortController === 'function' &&
      getSearchAbortController() === searchController
    ) {
      setSearchAbortController(null);
    }

    if (shouldApplyResponse() && typeof applyState === 'function') {
      applyState({ searchResultsLoading: false });
    }
  }
};

// Pagine di ricerca da 30 anche su mobile, dove la lista conversazioni puo'
// essersi allargata scorrendo (fino a 150: il server ne da' al massimo 100).
export const CHAT_LIST_SEARCH_PAGE_SIZE = 30;

export const buildChatListSearchResultsLoadContext = ({
  append = false,
  component = null,
  contactId = null,
  createAbortController = null,
  isAbortError = null,
  page = 1,
  query = '',
  searchConversations = null,
} = {}) => {
  if (!component) {
    return {
      append: false,
      getSearchResults: null,
      abortSearchRequest: null,
      applyState: null,
      clearBulkSelection: null,
      clearSearchResults: null,
      contactId,
      createAbortController,
      getCurrentPage: null,
      getIsComponentDestroyed: null,
      getSearchAbortController: null,
      getSearchResultsRequestId: null,
      isAbortError,
      itemsPerPage: 30,
      page,
      query,
      searchConversations,
      searchResultsRequestId: 0,
      selectedAssigneeIdFilter: null,
      setSearchAbortController: null,
      syncSearchRouteQuery: null,
    };
  }

  return {
    append,
    getSearchResults: () => component.searchResults,
    abortSearchRequest: () => component.abortSearchRequest?.(),
    applyState: (state) => Object.assign(component, state),
    clearBulkSelection: () => component.clearBulkSelection?.(),
    clearSearchResults: () => component.clearSearchResults?.(),
    contactId,
    createAbortController,
    getCurrentPage: () => component.currentPage,
    getIsComponentDestroyed: () => component.isComponentDestroyed,
    getSearchAbortController: () => component.searchAbortController,
    getSearchResultsRequestId: () => component.searchResultsRequestId,
    isAbortError,
    itemsPerPage: component.isMobileWeb
      ? CHAT_LIST_SEARCH_PAGE_SIZE
      : component.itemsPerPage,
    page,
    query,
    searchConversations,
    searchResultsRequestId: component.searchResultsRequestId,
    selectedAssigneeIdFilter: component.selectedAssigneeIdFilter,
    setSearchAbortController: (controller) => {
      component.searchAbortController = controller;
    },
    syncSearchRouteQuery: (nextQuery) =>
      component.syncSearchRouteQuerySafely?.(nextQuery),
  };
};

export const buildChatListSearchClearState = () => ({
  activeSearchQuery: '',
  isSearchModeActive: false,
  searchQuery: '',
  searchResults: [],
  searchResultsError: false,
  searchResultsLoading: false,
  searchResultsTotal: 0,
});

export const buildChatListResetBeforeFetchState = () => ({
  filterContactId: null,
  searchQuery: '',
});

export const runChatListFetchSafely = async ({
  fetchConversations = null,
  isSilent = false,
  page = 1,
} = {}) => {
  if (typeof fetchConversations !== 'function') {
    return false;
  }

  try {
    await fetchConversations(page, isSilent);
    return true;
  } catch (error) {
    return false;
  }
};

export const runChatListArchivedConversationsDirectFetch = async ({
  fetchConversationsSafely = null,
  page = 1,
  setFetchingConversations = null,
  setPaginationLoadingStatus = null,
} = {}) => {
  if (
    typeof fetchConversationsSafely !== 'function' ||
    typeof setFetchingConversations !== 'function' ||
    typeof setPaginationLoadingStatus !== 'function'
  ) {
    return { status: 'invalid' };
  }

  setFetchingConversations(true);
  setPaginationLoadingStatus(true);

  try {
    await fetchConversationsSafely(page);
    return { page, status: 'fetched' };
  } finally {
    setFetchingConversations(false);
    setPaginationLoadingStatus(false);
  }
};

export const runChatListResetAndFetchData = async ({
  activeAssigneeTab = null,
  activeSortBy = null,
  activeStatus = null,
  applyFilters = null,
  applyResetState = null,
  baseFilters = {},
  buildFilters = null,
  buildResetState = buildChatListResetBeforeFetchState,
  commitLoadingStatus = null,
  conversationType = '',
  fetchConversationsSafely = null,
  filterContactId = null,
  includeLabeledConversations = false,
  itemsPerPage = 30,
  label = '',
  priorityFilterEnabled = false,
  resetBulkActions = null,
  resetPage = null,
  routeQuery = {},
  selectedAssigneeIdFilter = null,
  setConversationFilters = null,
  setIncludeLabeledConversations = null,
} = {}) => {
  if (typeof resetBulkActions === 'function') {
    resetBulkActions();
  }

  if (
    typeof applyResetState === 'function' &&
    typeof buildResetState === 'function'
  ) {
    applyResetState(buildResetState());
  }

  if (typeof resetPage === 'function') {
    resetPage();
  }

  try {
    if (typeof commitLoadingStatus === 'function') {
      commitLoadingStatus();
    }
  } catch (_) {
    // Loading state is best-effort before the fetch.
  }

  if (typeof buildFilters !== 'function') {
    return { status: 'invalid' };
  }

  const savedIncludeLabeledValue = includeLabeledConversations;
  const nextFilters = buildFilters({
    activeAssigneeTab,
    activeStatus,
    activeSortBy,
    baseFilters,
    conversationType,
    currentPage: 1,
    filterContactId,
    includeLabeledConversations: savedIncludeLabeledValue,
    itemsPerPage,
    label,
    priorityFilterEnabled,
    routeQuery,
    selectedAssigneeIdFilter,
  });

  if (typeof applyFilters === 'function') {
    applyFilters(nextFilters);
  }
  if (typeof setConversationFilters === 'function') {
    setConversationFilters([]);
  }
  if (typeof setIncludeLabeledConversations === 'function') {
    setIncludeLabeledConversations(savedIncludeLabeledValue);
  }

  if (typeof fetchConversationsSafely !== 'function') {
    return { filters: nextFilters, status: 'invalid' };
  }

  const fetched = await fetchConversationsSafely();
  return {
    fetched,
    filters: nextFilters,
    status: fetched ? 'fetched' : 'failed',
  };
};

export const runChatListFilteredConversationsLoad = async ({
  currentFiltersPage = 0,
  emitConversationLoaded = null,
  fetchFilteredConversations = null,
  generateQueryData = null,
  payload = null,
  setAdvancedFiltersVisible = null,
} = {}) => {
  const page = currentFiltersPage + 1;

  try {
    if (
      typeof fetchFilteredConversations !== 'function' ||
      typeof generateQueryData !== 'function'
    ) {
      return { page, status: 'invalid' };
    }

    await fetchFilteredConversations({
      queryData: generateQueryData(payload),
      page,
    });

    if (typeof emitConversationLoaded === 'function') {
      emitConversationLoaded();
    }

    return { page, status: 'loaded' };
  } catch (_) {
    return { page, status: 'failed' };
  } finally {
    if (typeof setAdvancedFiltersVisible === 'function') {
      setAdvancedFiltersVisible(false);
    }
  }
};

export const buildChatListStatusRouteUpdate = ({
  currentRoute = {},
  inboxId = null,
  status = 'open',
} = {}) => {
  let routeName = 'home';
  if (currentRoute.name?.includes?.('inbox')) {
    routeName = currentRoute.name;
  } else if (inboxId) {
    routeName = 'inbox_dashboard';
  }

  const params = { ...(currentRoute.params || {}) };
  const query = { ...(currentRoute.query || {}) };

  if (inboxId) {
    params.inbox_id = inboxId;
  }

  if (status && status !== 'open') {
    query.status = status;
  } else {
    delete query.status;
  }

  return {
    name: routeName,
    params,
    query,
  };
};

export const shouldNavigateChatListRoute = ({
  currentFullPath = '',
  resolvedHref = '',
} = {}) => currentFullPath !== resolvedHref;

export const shouldClearChatListSearchResults = ({
  activeSearchQuery = '',
  isSearchModeActive = false,
} = {}) => Boolean(isSearchModeActive || activeSearchQuery);

export const buildChatListSearchClearedRouteQuery = (currentQuery = null) => {
  if (
    !currentQuery ||
    !Object.prototype.hasOwnProperty.call(currentQuery, 'q')
  ) {
    return null;
  }
  const nextQuery = { ...currentQuery };
  delete nextQuery.q;
  return nextQuery;
};

export const runChatListClearSearchResults = ({
  abortSearchRequest = null,
  activeSearchQuery = '',
  applyState = null,
  clearBulkSelection = null,
  currentRouteQuery = null,
  isSearchModeActive = false,
  replaceRouteQuery = null,
  searchResultsRequestId = 0,
  setSearchResultsRequestId = null,
} = {}) => {
  if (
    !shouldClearChatListSearchResults({
      activeSearchQuery,
      isSearchModeActive,
    })
  ) {
    return { status: 'skipped' };
  }

  if (typeof abortSearchRequest === 'function') {
    abortSearchRequest();
  }

  const nextRequestId = searchResultsRequestId + 1;
  if (typeof setSearchResultsRequestId === 'function') {
    setSearchResultsRequestId(nextRequestId);
  }

  const clearState = buildChatListSearchClearState();
  if (typeof applyState === 'function') {
    applyState(clearState);
  }

  if (typeof clearBulkSelection === 'function') {
    clearBulkSelection();
  }

  const clearedRouteQuery =
    buildChatListSearchClearedRouteQuery(currentRouteQuery);
  if (clearedRouteQuery && typeof replaceRouteQuery === 'function') {
    replaceRouteQuery(clearedRouteQuery);
  }

  return {
    clearedRouteQuery,
    searchResultsRequestId: nextRequestId,
    status: 'cleared',
  };
};

export const resolveChatListInitialRoutePlan = ({ routeQuery = {} } = {}) => {
  const query = (routeQuery?.q || '').toString().trim();
  if (!query) {
    return { mode: CHAT_LIST_INITIAL_ROUTE_BACKGROUND_MODE };
  }

  const parsedPage = parseInt(routeQuery?.page, 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  return { mode: CHAT_LIST_INITIAL_ROUTE_SEARCH_MODE, query, page };
};

export const buildChatListPageChangePlan = ({
  activeSearchQuery = '',
  conversationFilters = {},
  currentPage = 1,
  includeLabeledConversations = false,
  isSearchModeActive = false,
  newPage = 1,
  searchQuery = '',
} = {}) => {
  if (newPage === currentPage) {
    return { status: CHAT_LIST_PAGE_CHANGE_NOOP_STATUS };
  }

  const safePage = newPage < 1 ? 1 : newPage;

  if (isSearchModeActive) {
    const query = (activeSearchQuery || searchQuery || '').toString().trim();
    return {
      status: CHAT_LIST_PAGE_CHANGE_SEARCH_STATUS,
      page: safePage,
      query,
    };
  }

  const filters = {
    ...conversationFilters,
    page: safePage,
  };
  if (includeLabeledConversations) {
    filters.include_labeled = CHAT_LIST_INCLUDE_LABELED_FILTER_FLAG;
  }

  return {
    status: CHAT_LIST_PAGE_CHANGE_NORMAL_STATUS,
    page: safePage,
    filters,
  };
};

export const runChatListPageChange = async ({
  activeSearchQuery = '',
  conversationFilters = {},
  currentPage = 1,
  fetchConversationsSafely = null,
  getIsFetchingConversations = () => false,
  includeLabeledConversations = false,
  isSearchModeActive = false,
  loadSearchResults = null,
  newPage = 1,
  searchQuery = '',
  setCurrentPage = null,
  setPaginationLoading = null,
  updateFilters = null,
} = {}) => {
  const plan = buildChatListPageChangePlan({
    activeSearchQuery,
    conversationFilters,
    currentPage,
    includeLabeledConversations,
    isSearchModeActive,
    newPage,
    searchQuery,
  });

  if (plan.status === CHAT_LIST_PAGE_CHANGE_NOOP_STATUS) {
    return plan;
  }

  if (typeof setCurrentPage === 'function') {
    setCurrentPage(plan.page);
  }
  if (typeof setPaginationLoading === 'function') {
    setPaginationLoading(true);
  }

  if (plan.status === CHAT_LIST_PAGE_CHANGE_SEARCH_STATUS) {
    try {
      if (plan.query && typeof loadSearchResults === 'function') {
        await loadSearchResults(plan.query, plan.page);
      }
    } catch (error) {
      // Preserve the component's existing silent pagination failure handling.
    } finally {
      if (typeof setPaginationLoading === 'function') {
        setPaginationLoading(false);
      }
    }
    return plan;
  }

  if (typeof updateFilters === 'function') {
    updateFilters(plan.filters);
  }

  try {
    if (typeof fetchConversationsSafely === 'function') {
      await fetchConversationsSafely(plan.page);
    }
  } finally {
    const isFetching =
      typeof getIsFetchingConversations === 'function'
        ? getIsFetchingConversations()
        : false;
    if (!isFetching && typeof setPaginationLoading === 'function') {
      setPaginationLoading(false);
    }
  }

  return plan;
};
